// ============================================================================
// AUTHENTICATION API TYPES
// ============================================================================
// Types for Authentication and Authorization module

import { ID } from './common.types';

// ============================================================================
// TENANT HUB AUTHENTICATION TYPES (Two-Step Flow)
// ============================================================================

/**
 * Application ID from environment
 */
export const APPLICATION_ID = process.env.REACT_APP_APPLICATION_ID || '';

/**
 * Step 1: Discover Request
 */
export interface DiscoverRequest {
  username: string;
  password: string;
  clientId: string;
  tenantId?: string;
}

/**
 * Step 1: Discover Response
 */
export interface DiscoverResponse {
  userId: string;
  username: string;
  email?: string;
  accessibleTenants: AccessibleTenant[];
  recommendedTenant?: AccessibleTenant;
  totalTenants: number;
  totalApplications: number;
  validatedRealms: string[];
}

/**
 * Accessible Tenant
 */
export interface AccessibleTenant {
  tenantId: string;
  tenantCode: string;
  tenantName: string;
  tenantDisplayName?: string;
  privilegeLevel: string;
  isPrimaryTenant: boolean;
  realmType: string;
  realmName: string;
  applications: AccessibleApplication[];
  applicationCount: number;
}

/**
 * Accessible Application
 */
export interface AccessibleApplication {
  applicationId: string;
  applicationName: string;
  applicationCode: string;
  applicationVersion?: string;
  clientId: string;
  callbackUrl: string;
  logoutUrl: string;
  subscriptionPlan?: string;
  isActive: boolean;
  description?: string;
  icon?: string;
  category?: string;
}

/**
 * Step 2: Authenticate Request
 */
export interface AuthenticateRequest {
  username: string;
  password: string;
  selectedTenantId: string;
  selectedApplicationId: string;
}

/**
 * Step 2: Authenticate Response
 */
export interface AuthenticateResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  userRole: string;

  tenantId?: string;
  tenantCode?: string;
  tenantName?: string;
  tenantDisplayName?: string;
  tenantLogoBase64?: string;

  applicationId?: string;
  applicationCode?: string;
  applicationName?: string;

  realmId?: string;
  realmName?: string;

  issuedAt: string;
  expiresAt?: string;

  userId?: string;
  userName?: string;
  userFirstName?: string;
  userMiddleName?: string;
  userLastName?: string;
  userEmail?: string;

  permissions?: UserPermissionContext;
}

/**
 * User Permission Context
 */
export interface UserPermissionContext {
  roles: UserRoleInfo[];
  scopeConfiguration: UserScopeConfiguration;
  modules: AccessibleModule[];
  summary: PermissionSummary;
}

/**
 * User Role Info
 */
export interface UserRoleInfo {
  id: string;
  code: string;
  name: string;
  roleType: string;
  description?: string;
  assignmentType: string;
}

/**
 * User Scope Configuration
 */
export interface UserScopeConfiguration {
  primaryEntityId?: string;
  primaryBusinessUnitId?: string;
  hasTenantWideAccess: boolean;
  accessibleEntities: AccessibleEntity[];
  accessibleBusinessUnits: AccessibleBusinessUnit[];
  accessibleFinancialYears: AccessibleFinancialYear[];
}

/**
 * Accessible Entity
 */
export interface AccessibleEntity {
  id: string;
  entityCode: string;
  entityName: string;
  entityType: string;
  parentEntityId?: string;
  isPrimary: boolean;
  allowedActions: string[];
}

/**
 * Accessible Business Unit
 */
export interface AccessibleBusinessUnit {
  id: string;
  entityId: string;
  buCode: string;
  buName: string;
  buType: string;
  parentBuId?: string;
  isPrimary: boolean;
  allowedActions: string[];
}

/**
 * Accessible Financial Year
 */
export interface AccessibleFinancialYear {
  id: string;
  entityId: string;
  fyCode: string;
  fyName: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  isClosed: boolean;
  allowedActions: string[];
}

/**
 * Accessible Module
 */
export interface AccessibleModule {
  id: string;
  moduleCode: string;
  moduleName: string;
  description?: string;
  icon?: string;
  displayOrder: number;
  allowedActions: string[];
  menus: AccessibleMenu[];
}

/**
 * Accessible Menu
 */
export interface AccessibleMenu {
  id: string;
  menuCode: string;
  menuName: string;
  menuType: string;
  menuAction?: string;
  icon?: string;
  displayOrder: number;
  parentMenuId?: string;
  allowedActions: string[];
  children: AccessibleMenu[];
  uis: AccessibleUI[];
}

/**
 * Accessible UI
 */
export interface AccessibleUI {
  id: string;
  uiCode: string;
  uiName: string;
  uiPath?: string;
  uiType: string;
  description?: string;
  icon?: string;
  displayOrder: number;
  allowedActions: string[];
  elements: AccessibleElement[];
}

/**
 * Accessible Element
 */
export interface AccessibleElement {
  id: string;
  elementCode: string;
  elementName: string;
  elementType: string;
  elementAction?: string;
  parentElementId?: string;
  isVisible: boolean;
  isEnabled: boolean;
  allowedActions: string[];
}

/**
 * Permission Summary
 */
export interface PermissionSummary {
  totalRoles: number;
  accessibleEntities: number;
  accessibleBusinessUnits: number;
  accessibleFinancialYears: number;
  accessibleModules: number;
  accessibleMenus: number;
  accessibleUIs: number;
  accessibleElements: number;
}

/**
 * Refresh Hub Token Request
 */
export interface RefreshHubTokenRequest {
  refreshToken: string;
  tenantId: string;
  applicationId: string;
}

// ============================================================================
// LEGACY AUTHENTICATION TYPES (Backward Compatibility)
// ============================================================================

/**
 * User Role
 */
export type UserRole =
  | 'Admin'
  | 'Manager'
  | 'User'
  | 'Viewer'
  | 'Guest';

/**
 * Permission
 */
export type Permission = string; // e.g., 'services.create', 'users.read'

/**
 * User
 */
export interface User {
  id: ID;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: UserRole;
  permissions: Permission[];
  entityIds: ID[]; // Entities user has access to
  isActive: boolean;
  lastLogin?: string;
  profilePicture?: string;
  createdAt: string;
}

/**
 * Login Request
 */
export interface LoginRequest {
  username: string;
  password: string;
  rememberMe?: boolean;
}

/**
 * Login Response
 */
export interface LoginResponse {
  success: boolean;
  data: {
    user: User;
    token: string;
    refreshToken: string;
    expiresIn: number; // Seconds until token expires
  };
  message?: string;
}

/**
 * Register Request
 */
export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: UserRole;
}

/**
 * Register Response
 */
export interface RegisterResponse {
  success: boolean;
  data: {
    user: User;
    message: string;
  };
}

/**
 * Refresh Token Request
 */
export interface RefreshTokenRequest {
  refreshToken: string;
}

/**
 * Refresh Token Response
 */
export interface RefreshTokenResponse {
  success: boolean;
  data: {
    token: string;
    refreshToken: string;
    expiresIn: number;
  };
}

/**
 * Logout Request
 */
export interface LogoutRequest {
  refreshToken?: string;
}

/**
 * Logout Response
 */
export interface LogoutResponse {
  success: boolean;
  message: string;
}

/**
 * Change Password Request
 */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Forgot Password Request
 */
export interface ForgotPasswordRequest {
  email: string;
}

/**
 * Reset Password Request
 */
export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Update Profile Request
 */
export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  profilePicture?: string;
}

/**
 * User Session
 */
export interface UserSession {
  sessionId: string;
  userId: ID;
  ipAddress: string;
  userAgent: string;
  loginTime: string;
  lastActivity: string;
  isActive: boolean;
}

/**
 * Permission Check Request
 */
export interface PermissionCheckRequest {
  permission: Permission;
  resourceId?: ID;
}

/**
 * Permission Check Response
 */
export interface PermissionCheckResponse {
  hasPermission: boolean;
  reason?: string;
}
